import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDropzone } from 'react-dropzone';
import { useMutation } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Upload, X, Eye, Clock, Tag } from 'lucide-react';
import { Category, DEFAULT_DECAY_CURVE } from '@run2sale/shared';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Textarea } from '@/components/ui/Textarea';
import { Select } from '@/components/ui/Select';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Spinner } from '@/components/ui/Spinner';
import { dealService, type CreateDealData } from '@/services/deal.service';
import { cn } from '@/lib/utils';

const CATEGORIES = Object.entries(Category).map(([key, value]) => ({
  value,
  label: key,
}));

const COMMON_SIZES = ['XS', 'S', 'M', 'L', 'XL', 'XXL', '36', '37', '38', '39', '40', '41', '42', '43', '44', '45'];
const COMMON_COLORS = ['Black', 'White', 'Red', 'Blue', 'Green', 'Navy', 'Grey', 'Beige', 'Pink', 'Brown'];

export function CreateDealPage() {
  const navigate = useNavigate();
  const [showPreview, setShowPreview] = useState(false);

  // Form state
  const [itemName, setItemName] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState<string>(Category.Fashion);
  const [photoFile, setPhotoFile] = useState<File | null>(null);
  const [photoPreview, setPhotoPreview] = useState<string>('');
  const [originalPrice, setOriginalPrice] = useState<string>('');
  const [selectedSizes, setSelectedSizes] = useState<string[]>([]);
  const [selectedColors, setSelectedColors] = useState<string[]>([]);
  const [slotCount, setSlotCount] = useState<string>('5');
  const [publishNow, setPublishNow] = useState(true);
  const [scheduledFor, setScheduledFor] = useState('');
  const [useCustomDecay, setUseCustomDecay] = useState(false);
  const [decayTiers, setDecayTiers] = useState(
    DEFAULT_DECAY_CURVE.tiers.map((t) => ({ ...t }))
  );

  // Photo upload dropzone
  const onDrop = useCallback((acceptedFiles: File[]) => {
    const file = acceptedFiles[0];
    if (file) {
      setPhotoFile(file);
      const reader = new FileReader();
      reader.onload = () => setPhotoPreview(reader.result as string);
      reader.readAsDataURL(file);
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { 'image/*': ['.png', '.jpg', '.jpeg', '.webp'] },
    maxFiles: 1,
    maxSize: 5 * 1024 * 1024,
  });

  const removePhoto = () => {
    setPhotoFile(null);
    setPhotoPreview('');
  };

  // Toggle helpers
  const toggleSize = (size: string) => {
    setSelectedSizes((prev) =>
      prev.includes(size) ? prev.filter((s) => s !== size) : [...prev, size]
    );
  };

  const toggleColor = (color: string) => {
    setSelectedColors((prev) =>
      prev.includes(color) ? prev.filter((c) => c !== color) : [...prev, color]
    );
  };

  const updateDecayTier = (index: number, field: 'discountPercent', value: number) => {
    setDecayTiers((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  // Create deal mutation
  const createMutation = useMutation({
    mutationFn: async () => {
      let photoUrl: string | undefined;
      if (photoFile) {
        const result = await dealService.uploadPhoto(photoFile);
        photoUrl = result.url;
      }

      const expiresAfterMinutes = decayTiers[decayTiers.length - 1].toMinutes;

      const data: CreateDealData = {
        itemName,
        description,
        category,
        photoUrl,
        originalPrice: parseFloat(originalPrice),
        currency: 'AZN',
        sizes: selectedSizes,
        colors: selectedColors,
        slotCount: parseInt(slotCount, 10),
        decayCurve: {
          tiers: decayTiers,
          expiresAfterMinutes,
        },
        publishNow,
        scheduledFor: publishNow ? undefined : scheduledFor,
      };

      return dealService.createDeal(data);
    },
    onSuccess: () => {
      toast.success('Deal created successfully!');
      navigate('/deals/active');
    },
    onError: (err: Error) => {
      toast.error(err.message || 'Failed to create deal');
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!itemName || !originalPrice || !slotCount) {
      toast.error('Please fill in all required fields');
      return;
    }
    createMutation.mutate();
  };

  const price = parseFloat(originalPrice) || 0;
  const currentDiscount = decayTiers[0]?.discountPercent || 50;
  const discountedPrice = price * (1 - currentDiscount / 100);

  return (
    <div className="mx-auto max-w-6xl">
      <div className="mb-6 flex items-center justify-between">
        <h1 className="text-3xl font-bold">Create New Deal</h1>
        <Button variant="outline" onClick={() => setShowPreview(!showPreview)}>
          <Eye className="mr-2 h-4 w-4" />
          {showPreview ? 'Hide Preview' : 'Show Preview'}
        </Button>
      </div>

      <div className={cn('grid gap-6', showPreview ? 'lg:grid-cols-3' : 'lg:grid-cols-2')}>
        {/* Form */}
        <form onSubmit={handleSubmit} className={showPreview ? 'lg:col-span-2' : 'lg:col-span-2'}>
          <div className="space-y-6">
            {/* Item Details */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Item Details</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <Input
                  label="Item Name *"
                  placeholder="e.g., Nike Air Max 90"
                  value={itemName}
                  onChange={(e) => setItemName(e.target.value)}
                />
                <Textarea
                  label="Description"
                  placeholder="Describe the item..."
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  rows={3}
                />
                <Select
                  label="Category *"
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                  options={[{ value: '', label: 'Select category' }, ...CATEGORIES]}
                />

                {/* Photo Upload */}
                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700">Photo</label>
                  {photoPreview ? (
                    <div className="relative inline-block">
                      <img
                        src={photoPreview}
                        alt="Item preview"
                        className="h-40 w-40 rounded-lg object-cover"
                      />
                      <button
                        type="button"
                        onClick={removePhoto}
                        className="absolute -right-2 -top-2 rounded-full bg-destructive p-1 text-white"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  ) : (
                    <div
                      {...getRootProps()}
                      className={cn(
                        'flex cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed p-8 transition-colors',
                        isDragActive
                          ? 'border-primary bg-primary/5'
                          : 'border-gray-300 hover:border-primary/50'
                      )}
                    >
                      <input {...getInputProps()} />
                      <Upload className="mb-2 h-8 w-8 text-gray-400" />
                      <p className="text-sm text-gray-500">
                        {isDragActive
                          ? 'Drop the image here...'
                          : 'Drag & drop an image, or click to select'}
                      </p>
                      <p className="mt-1 text-xs text-gray-400">PNG, JPG, WebP up to 5MB</p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Pricing */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Pricing</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-end gap-4">
                  <Input
                    label="Original Price *"
                    type="number"
                    step="0.01"
                    min="0"
                    placeholder="0.00"
                    value={originalPrice}
                    onChange={(e) => setOriginalPrice(e.target.value)}
                  />
                  <div className="flex h-10 items-center rounded-md border bg-muted px-3 text-sm font-medium">
                    AZN
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Inventory */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Inventory</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700">
                    Sizes (select available)
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {COMMON_SIZES.map((size) => (
                      <button
                        key={size}
                        type="button"
                        onClick={() => toggleSize(size)}
                        className={cn(
                          'rounded-full border px-3 py-1 text-sm transition-colors',
                          selectedSizes.includes(size)
                            ? 'border-primary bg-primary text-white'
                            : 'border-gray-300 hover:border-primary/50'
                        )}
                      >
                        {size}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700">
                    Colors (select available)
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {COMMON_COLORS.map((color) => (
                      <button
                        key={color}
                        type="button"
                        onClick={() => toggleColor(color)}
                        className={cn(
                          'rounded-full border px-3 py-1 text-sm transition-colors',
                          selectedColors.includes(color)
                            ? 'border-primary bg-primary text-white'
                            : 'border-gray-300 hover:border-primary/50'
                        )}
                      >
                        {color}
                      </button>
                    ))}
                  </div>
                </div>

                <Input
                  label="Number of Claim Slots *"
                  type="number"
                  min="1"
                  max="100"
                  value={slotCount}
                  onChange={(e) => setSlotCount(e.target.value)}
                />
              </CardContent>
            </Card>

            {/* Decay Curve */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Clock className="h-5 w-5" />
                  Discount Decay Curve
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="customDecay"
                    checked={useCustomDecay}
                    onChange={(e) => {
                      setUseCustomDecay(e.target.checked);
                      if (!e.target.checked) {
                        setDecayTiers(DEFAULT_DECAY_CURVE.tiers.map((t) => ({ ...t })));
                      }
                    }}
                    className="h-4 w-4 rounded border-gray-300"
                  />
                  <label htmlFor="customDecay" className="text-sm">
                    Customize decay curve (default: 50% - 40% - 20% - 10%)
                  </label>
                </div>

                {/* Decay visualization */}
                <div className="rounded-lg bg-gray-50 p-4">
                  <div className="flex items-end gap-1" style={{ height: 120 }}>
                    {decayTiers.map((tier, i) => {
                      const widthPercent =
                        ((tier.toMinutes - tier.fromMinutes) /
                          decayTiers[decayTiers.length - 1].toMinutes) *
                        100;
                      const heightPercent = (tier.discountPercent / 60) * 100;
                      const colors = ['bg-green-500', 'bg-yellow-500', 'bg-orange-500', 'bg-red-500'];
                      return (
                        <div
                          key={i}
                          className="relative flex flex-col items-center justify-end"
                          style={{ width: `${widthPercent}%`, height: '100%' }}
                        >
                          <span className="mb-1 text-xs font-bold">{tier.discountPercent}%</span>
                          <div
                            className={cn('w-full rounded-t', colors[i] || 'bg-gray-400')}
                            style={{ height: `${heightPercent}%` }}
                          />
                          <span className="mt-1 text-xs text-gray-500">
                            {tier.fromMinutes}-{tier.toMinutes}m
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {useCustomDecay && (
                  <div className="space-y-3">
                    {decayTiers.map((tier, i) => (
                      <div key={i} className="flex items-center gap-3">
                        <span className="w-24 text-sm text-gray-600">
                          {tier.fromMinutes}-{tier.toMinutes} min
                        </span>
                        <input
                          type="range"
                          min="5"
                          max="60"
                          step="5"
                          value={tier.discountPercent}
                          onChange={(e) =>
                            updateDecayTier(i, 'discountPercent', parseInt(e.target.value, 10))
                          }
                          className="flex-1"
                        />
                        <span className="w-12 text-right text-sm font-medium">
                          {tier.discountPercent}%
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Scheduling */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Scheduling</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center gap-4">
                  <label className="flex items-center gap-2">
                    <input
                      type="radio"
                      name="publishTime"
                      checked={publishNow}
                      onChange={() => setPublishNow(true)}
                      className="h-4 w-4"
                    />
                    <span className="text-sm">Publish Now</span>
                  </label>
                  <label className="flex items-center gap-2">
                    <input
                      type="radio"
                      name="publishTime"
                      checked={!publishNow}
                      onChange={() => setPublishNow(false)}
                      className="h-4 w-4"
                    />
                    <span className="text-sm">Schedule for Later</span>
                  </label>
                </div>

                {!publishNow && (
                  <Input
                    label="Publish Date & Time"
                    type="datetime-local"
                    value={scheduledFor}
                    onChange={(e) => setScheduledFor(e.target.value)}
                    min={new Date().toISOString().slice(0, 16)}
                  />
                )}
              </CardContent>
            </Card>

            {/* Submit */}
            <div className="flex gap-4">
              <Button type="submit" disabled={createMutation.isPending} className="flex-1">
                {createMutation.isPending ? (
                  <>
                    <Spinner className="mr-2 h-4 w-4" />
                    Creating...
                  </>
                ) : publishNow ? (
                  'Publish Deal Now'
                ) : (
                  'Schedule Deal'
                )}
              </Button>
              <Button type="button" variant="outline" onClick={() => navigate('/')}>
                Cancel
              </Button>
            </div>
          </div>
        </form>

        {/* Preview Panel */}
        {showPreview && (
          <div className="lg:col-span-1">
            <div className="sticky top-6">
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Shopper Preview</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="overflow-hidden rounded-lg border">
                    {photoPreview ? (
                      <img
                        src={photoPreview}
                        alt="Preview"
                        className="h-48 w-full object-cover"
                      />
                    ) : (
                      <div className="flex h-48 items-center justify-center bg-gray-100">
                        <Tag className="h-12 w-12 text-gray-300" />
                      </div>
                    )}
                    <div className="p-4">
                      <h3 className="font-semibold">{itemName || 'Item Name'}</h3>
                      <p className="mt-1 text-sm text-gray-500">
                        {description || 'Item description...'}
                      </p>
                      <div className="mt-3 flex items-center gap-2">
                        <span className="text-lg font-bold text-green-600">
                          {discountedPrice.toFixed(2)} AZN
                        </span>
                        {price > 0 && (
                          <span className="text-sm text-gray-400 line-through">
                            {price.toFixed(2)} AZN
                          </span>
                        )}
                      </div>
                      {currentDiscount > 0 && (
                        <span className="mt-2 inline-block rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-700">
                          {currentDiscount}% OFF
                        </span>
                      )}
                      <div className="mt-3 flex flex-wrap gap-1">
                        {selectedSizes.map((s) => (
                          <span
                            key={s}
                            className="rounded bg-gray-100 px-2 py-0.5 text-xs text-gray-600"
                          >
                            {s}
                          </span>
                        ))}
                      </div>
                      <div className="mt-2 flex items-center gap-2 text-sm text-gray-500">
                        <span>{slotCount || 0} slots available</span>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
